import React, { useState } from 'react';
import { Button, Divider, Form, Input, Modal } from 'antd';
import { CheckCircleIcon, CloseXIcon, InfoCircleIcon } from 'icons';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { PatternFormat } from 'react-number-format';

import { useAppSelector } from 'store';

import { globalConst, useCreateApplication } from 'modules/global';

import SuccessModal from './success-modal';

interface IProps {
  ticketId?: number;
  onClose: () => void;
  open: boolean;
  score?: number;
  passingScore?: number;
}

const ApplicationModal: React.FC<IProps> = ({ ticketId, onClose, open, score, passingScore }) => {
  const t = useTranslations();
  const { push } = useRouter();
  const { mutate, isLoading: isSendLoading } = useCreateApplication();
  const [form] = Form.useForm();
  const user = useAppSelector(state => state.user);
  const [successVisible, setSuccessVisible] = useState(false);

  const onFinish = (values: { phone: string; user_name: string }) => {
    const sanitizedPhone = values.phone.replace(/\s|-/g, '');
    const isPhoneValid = sanitizedPhone?.length === 13;

    if (isPhoneValid) {
      mutate(
        { ...values, phone: sanitizedPhone, ticket_id: ticketId },
        {
          onSuccess: () => {
            setSuccessVisible(true);
            onClose()
            setTimeout(() => {
              setSuccessVisible(false);
              push(`${globalConst.pageNames.RESULTS}/${ticketId}`);
            }, 2000);
          }
        }
      );
    } else {
      form.setFields([
        {
          name: 'phone',
          errors: [t('Togri_telefon_raqam_kiriting')]
        }
      ]);
    }
  };

  return (
    <>
      <Modal
        footer={false}
        open={open}
        centered
        onCancel={onClose}
        width="400px"
        closeIcon={
          <div className="flex items-center justify-center rounded-full bg-gray-800 p-1">
            <CloseXIcon size="12" />
          </div>
        }
        styles={{
          content: { padding: '16px', borderRadius: '12px' }
        }}
        title={<span className="text-lg">{t('ArizaQoldirish')}</span>}
      >
        <Form
          initialValues={{
            user_name: `${user?.name} ${user.last_name}` || '',
            phone: user?.phone || '+998'
          }}
          form={form}
          onFinish={onFinish}
        >
          <Form.Item
            name="user_name"
            rules={[
              {
                required: true,
                message: ''
              }
            ]}
          >
            <Input
              className="!rounded-xl !bg-[#f2f3f5] hover:!border-green-500 focus:!border-green-500"
              placeholder={t('auth.first_name_placeholder')}
            />
          </Form.Item>
          <Form.Item
            name="phone"
            rules={[
              {
                required: true
              }
            ]}
          >
            <PatternFormat
              format="+### ## ### ## ##"
              allowEmptyFormatting={true}
              mask=" "
              type="tel"
              placeholder={t('auth.phone_placeholder')}
              className={` !h-11 !rounded-xl !border !p-3 hover:!border-green-500 ${
                form.getFieldError('phone')?.length ? '!border-red-500' : '!border-transparent'
              } w-full !bg-gray-100 hover:!outline-green-500 focus:!outline-green-500`}
            />
          </Form.Item>
          <p className="mb-3 text-gray-800">
            {t('malumotlaringizni_unverstetga_yuboriladi_va_ular_siz_bilan_boglanishadi')}
          </p>
          <Divider className="!my-3" />
          {score && passingScore && score === passingScore ? (
            <div className="flex gap-2 rounded-xl bg-green-50 p-3">
              <div className="mt-1 flex-shrink-0 text-green-600">
                <CheckCircleIcon size="18" />
              </div>
              <div className="text-sm text-green-700">
                <p className="font-semibold">{t('tabriklaymiz_testni_muvaffaqiyatli_topshirdingiz')}</p>
                <p className="mt-1 text-xs">{t('endi_siz_oz_bilimingizni_amalda_namoyish_etishingiz_mumkin')}</p>
              </div>
            </div>
          ) : (
            <div className=" flex gap-2 rounded-xl bg-orange-50 p-3">
              <div className="mt-1 flex-shrink-0 text-orange-500">
                <InfoCircleIcon />
              </div>
              <p className="text-sm">
                {t(
                  'ariza_yuborish_imkoniyatingzi_bor_biroq_testni_qayta_yechib_yaxshiroq_natija_bilan_yuborishingizni_tavsiya_qilamiz'
                )}
              </p>
            </div>
          )}

          <Button
            loading={isSendLoading}
            htmlType="submit"
            className="!mt-5 !w-full !rounded-lg !border-none !bg-green-500 !px-7 !py-5 !text-white hover:!border-none"
          >
            {t('Yuborish')}
          </Button>
        </Form>
      </Modal>

      <SuccessModal
        open={successVisible}
        onClose={() => setSuccessVisible(false)}
        title={t('ariza_yuborildi')}
        desc={t('ariza_yuborildi_tavsif')}
      />
    </>
  );
};

export default ApplicationModal;
